import { useEffect, useState } from "react";
import { useAppRuntime } from "../providers/AppRuntimeProvider";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Sync progress per source — the typed SAME-UNIT pair from `sync.progress`.
 *  `discovered` is the connector's cumulative primary-item count (latest
 *  event wins — NEVER an accumulated envelope sum: `ingested` counts
 *  envelopes per cycle and double-counts across reconnects, and pairing it
 *  with `total` was the "62,548 / 2,593" StatusBar lie). */
export interface SyncProgress {
  readonly sourceId: string;
  readonly moduleId: string;
  /** Cumulative primary items discovered (same units as `total`); 0 = the
   *  source has not reported one. */
  readonly discovered: number;
  /** Estimated total primary items (from source, if available). */
  readonly total: number | null;
  readonly phase: string;
}

/** Index progress — separate from sync, shown as permanent indicator. */
export interface IndexProgress {
  readonly indexed: number;
  readonly total: number;
}

export interface UseSyncStatusResult {
  /** Latest status message to show in StatusBar (replaces previous). */
  readonly latestStatus: { readonly sourceId: string; readonly message: string } | null;
  /** Index progress — separate permanent indicator. */
  readonly indexProgress: IndexProgress | null;
  /** Accumulated sync progress per source (source_id → SyncProgress). */
  readonly syncProgress: ReadonlyMap<string, SyncProgress>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * The status bar's live feed: `sync.progress` (per-cycle, same-unit pair)
 * and `app.index_progress` (the search indexer). Push-based over the
 * WebSocket event stream — no RPC, no polling. (The application-log half
 * that used to live beside this — `logs.recent`, `app.error`, `app.status`
 * — was deleted with the user-facing log feature: the process log is read
 * by humans and agents, not by the app.)
 */
export function useSyncStatus(): UseSyncStatusResult {
  const runtime = useAppRuntime();
  const [syncProgress, setSyncProgress] = useState<
    ReadonlyMap<string, SyncProgress>
  >(new Map());
  const [latestStatus, setLatestStatus] = useState<{
    readonly sourceId: string;
    readonly message: string;
  } | null>(null);
  const [indexProgress, setIndexProgress] = useState<IndexProgress | null>(
    null,
  );

  useEffect(() => {
    // sync.progress — per-cycle events from the scheduler. The event carries
    // the typed SAME-UNIT pair (`discovered` = cumulative primary items,
    // `total` = primary items); latest event wins. `ingested` counts the
    // cycle's ENVELOPES — it is deliberately never read here: accumulating it
    // and pairing with total produced the "62,548 / 2,593" mixed-unit lie.
    const offSync = runtime.transport.onEventType(
      ["sync.progress"],
      (event) => {
        const payload = event.payload as {
          source_id?: string;
          module_id?: string;
          discovered?: number | null;
          total?: number | null;
          phase?: string;
        };
        const sourceId = payload.source_id ?? payload.module_id ?? "unknown";
        const phase = payload.phase ?? "syncing";
        const reportedDiscovered =
          payload.discovered != null && payload.discovered > 0 ? payload.discovered : null;
        const reportedTotal =
          payload.total != null && payload.total > 0 ? payload.total : null;
        setSyncProgress((prev) => {
          const next = new Map(prev);
          const existing = next.get(sourceId);
          // A cycle that reported nothing keeps the last known counts —
          // a catchup event must not wipe the bootstrap numbers.
          const discovered = reportedDiscovered ?? existing?.discovered ?? 0;
          const total = reportedTotal ?? existing?.total ?? null;
          next.set(sourceId, {
            sourceId,
            moduleId: payload.module_id ?? sourceId,
            discovered,
            total,
            phase,
          });
          // Numeric status ONLY from the same-unit pair; without a reported
          // `discovered` there is no number to show (status text stays).
          if (discovered > 0) {
            const msg =
              total != null && total > 0
                ? `${discovered.toLocaleString()} / ${total.toLocaleString()}`
                : `${discovered.toLocaleString()} synced`;
            setLatestStatus({ sourceId, message: msg });
          }
          return next;
        });
      },
    );

    // app.index_progress — dedicated indexer progress (separate indicator).
    // The bus message is `{type, indexed, total}` at the top level.
    const offIndex = runtime.transport.onEventType(
      ["app.index_progress"],
      (event) => {
        const payload = event.payload as {
          indexed?: number;
          total?: number;
        };
        if (payload.indexed != null && payload.total != null) {
          setIndexProgress({
            indexed: payload.indexed,
            total: payload.total,
          });
        }
      },
    );

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => {
      offSync();
      offIndex();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { latestStatus, indexProgress, syncProgress };
}
